import type React from "react";
import { useEffect } from "react";
import { isPresent } from "@apl-digital/utils";
import * as Sentry from "@sentry/react";

import type { ISalesPointConfiguration } from "@/api/interfaces/configurationLayouts";
import Loader from "@/base/components/Global/Loader";
import { isInIframe } from "@/base/utils/iframeUtils";
import { getTenantKey } from "@/base/utils/tenantKey";
import { ErrorPage } from "@/pages/ErrorPage";
import { useAppDispatch, useAppSelector } from "@/store";
import { shouldLoad } from "@/store/apiRequestStatusMachine";
import { loadSalesPointConfiguration } from "@/store/salesPoint";
import type { SalesPointState } from "@/store/salesPoint/types";
import {
  getTheatreResource,
  selectTheatreResource,
} from "@/store/theatreResource";
import { isStatus } from "@/store/utils";

import { getAvailableDates, getAvailableTimes } from "./actions";
import { SalesPointContext } from "./useSalesPointContext";

const SALESPOINT_CONFIGURATION_MAX_RETRY_COUNT = 3;

type SalesPointContextProviderProps = {
  children?: React.ReactNode;
};

type TelemetryAttributeValue = boolean | number | string | null | undefined;
type DefinedTelemetryAttributeValue = boolean | number | string;
type SalesPointConfigurationFailure = Extract<
  SalesPointState["configuration"],
  { status: "failed" | "failedPending" }
>;

const getDefinedTelemetryAttributes = (
  attributes: Record<string, TelemetryAttributeValue>,
) =>
  Object.fromEntries(
    Object.entries(attributes).filter(([, value]) => isPresent(value)),
  ) as Record<string, DefinedTelemetryAttributeValue>;

const getSalesPointFailureTelemetry = (
  configuration: SalesPointConfigurationFailure,
  visibleErrorPage: boolean,
) => {
  const failure = configuration.state;
  const request = failure?.request;
  const apiEndpoint = request?.api ?? "/salespoint";
  const method = request?.method ?? "GET";
  const responseStatus =
    failure?.responseStatus === undefined
      ? "none"
      : String(failure.responseStatus);
  const failureReason = failure?.reason ?? "unknown";
  const tenantKey = getTenantKey();
  const requestId = request?.requestId;
  const responseRequestId = request?.responseRequestId;
  const responseRequestIdEchoed = request?.responseRequestIdEchoed;
  const connectionEffectiveType = request?.client?.connectionEffectiveType;
  const salespointConfigurationContext = {
    client: request?.client,
    failureReason,
    request: {
      api: request?.api,
      language: request?.language,
      method,
      requestId,
      responseRequestId,
      responseRequestIdEchoed,
      shouldParseResponse: request?.shouldParseResponse,
    },
    resourceTiming: request?.resourceTiming,
    responseStatus,
    retryCount: configuration.retryCount,
    status: configuration.status,
  };
  const telemetryAttributes = {
    "api.endpoint": apiEndpoint,
    "api.failure_reason": failureReason,
    "api.method": method,
    "api.response_status": responseStatus,
    "client.connection_effective_type": connectionEffectiveType,
    "client.online": request?.client?.online,
    "client.visibility_state": request?.client?.visibilityState,
    "salespoint.status": configuration.status,
    "salespoint.visible_error_page": visibleErrorPage,
    "salespoint.will_retry":
      configuration.retryCount < SALESPOINT_CONFIGURATION_MAX_RETRY_COUNT,
    "tenant.key": tenantKey,
  };
  const diagnosticTelemetryAttributes = {
    ...telemetryAttributes,
    "api.request_id": requestId,
    "api.response_request_id": responseRequestId,
    "api.response_request_id_echoed": responseRequestIdEchoed,
    "salespoint.retry_count": configuration.retryCount,
  };

  return {
    apiEndpoint,
    diagnosticTelemetryAttributes,
    failureReason,
    responseStatus,
    salespointConfigurationContext,
    telemetryAttributes,
  };
};

const logRetryableSalesPointFailure = (
  configuration: SalesPointConfigurationFailure,
) => {
  const {
    diagnosticTelemetryAttributes,
    salespointConfigurationContext,
    telemetryAttributes,
  } = getSalesPointFailureTelemetry(configuration, false);

  Sentry.withScope((scope) => {
    const definedDiagnosticAttributes = getDefinedTelemetryAttributes(
      diagnosticTelemetryAttributes,
    );

    scope.setContext(
      "salespoint_configuration",
      salespointConfigurationContext,
    );

    for (const [key, value] of Object.entries(definedDiagnosticAttributes)) {
      scope.setTag(key, String(value));
    }

    try {
      Sentry.logger.error(
        "Sales point configuration failed while retrying",
        definedDiagnosticAttributes,
        { scope },
      );
    } catch {
      // Salespoint retry telemetry must not break the retry path.
    }

    try {
      Sentry.metrics.count(
        "order_app.salespoint_configuration.retryable_failure",
        1,
        {
          attributes: getDefinedTelemetryAttributes(telemetryAttributes),
          scope,
        },
      );
    } catch {
      // Salespoint retry telemetry must not break the retry path.
    }
  });
};

export const SalesPointContextProvider: React.FC<
  SalesPointContextProviderProps
> = ({ children }) => {
  const dispatch = useAppDispatch();
  const configuration = useAppSelector(
    (state) => state.salesPoint.configuration,
  );
  const tableInfo = useAppSelector(selectTheatreResource);

  useEffect(() => {
    if (isInIframe()) {
      return;
    }

    if (shouldLoad(configuration)) {
      void dispatch(loadSalesPointConfiguration());
    }

    if (isStatus(tableInfo, "stale")) {
      void dispatch(
        getTheatreResource({ barcodeWithoutChecksum: tableInfo.state.Barcode }),
      );
    }
  }, [configuration]);

  useEffect(() => {
    if (
      isInIframe() ||
      !isStatus(configuration, "failed") ||
      configuration.retryCount >= SALESPOINT_CONFIGURATION_MAX_RETRY_COUNT
    ) {
      return;
    }

    logRetryableSalesPointFailure(configuration);
  }, [configuration]);

  const isRetryingSalesPointConfiguration =
    !isInIframe() &&
    isStatus(configuration, ["failed", "failedPending"]) &&
    configuration.retryCount < SALESPOINT_CONFIGURATION_MAX_RETRY_COUNT;

  if (
    !isInIframe() &&
    (isStatus(configuration, ["pending", "idle"]) ||
      isRetryingSalesPointConfiguration)
  ) {
    return <Loader isFullScreen />;
  }

  if (!isInIframe() && isStatus(configuration, ["failed", "failedPending"])) {
    const {
      apiEndpoint,
      diagnosticTelemetryAttributes,
      failureReason,
      responseStatus,
      salespointConfigurationContext,
      telemetryAttributes,
    } = getSalesPointFailureTelemetry(configuration, true);

    return (
      <ErrorPage
        error={{
          type: "admin",
          messageForAdmin: () => {
            console.error(
              `Failed to load sales point configuration, got response code ${responseStatus} from API`,
            );
            console.error(failureReason);
          },
          sentry: {
            message: "Sales point configuration failed to load",
            fingerprint: [
              "error-page",
              "salespoint-configuration",
              apiEndpoint,
              String(responseStatus),
              failureReason,
            ],
            tags: diagnosticTelemetryAttributes,
            contexts: {
              salespoint_configuration: salespointConfigurationContext,
            },
            log: {
              message: "Sales point configuration failed to load",
              attributes: diagnosticTelemetryAttributes,
            },
            metrics: [
              {
                name: "order_app.salespoint_configuration.failure",
                attributes: telemetryAttributes,
              },
            ],
          },
        }}
      />
    );
  }

  return (
    <SalesPointContext.Provider
      value={[
        {
          configuration: configuration.state as ISalesPointConfiguration,
        },
        {
          getAvailableDates,
          getAvailableTimes,
        },
      ]}
    >
      {children}
    </SalesPointContext.Provider>
  );
};
